import Chip from '@mui/material/Chip';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Reveal } from '@/components/Reveal';

type Evidence = { icon: SvgIconComponent; label: string; note: string };

// Every line here is a verifiable fact from the repository, not a marketing estimate — see the
// linked blog posts for the detail behind each one. Deliberately not leading with lines-of-code;
// enforcement, isolation, and signed execution are the stronger signal.
const evidence: Evidence[] = [
  { icon: RuleOutlinedIcon, label: 'CI gates every merge', note: 'Classification, dependency audit, secret-scan, and the full test suite must pass before anything reaches main' },
  { icon: ScienceOutlinedIcon, label: '10.8k lines of test code', note: 'A 5.3% test-to-production ratio, computed automatically and published on our own README, not asserted' },
  { icon: SecurityOutlinedIcon, label: 'Tenant isolation at the database', note: 'PostgreSQL row-level security keyed per tenant — enforced by Postgres itself, not just application code' },
  { icon: VerifiedUserOutlinedIcon, label: 'Signed endpoint execution', note: 'Ed25519-signed remote jobs against a default-deny capability registry' },
  { icon: LockOutlinedIcon, label: 'Secrets encrypted, fail-closed', note: 'Integration credentials are encrypted at rest; production refuses to run misconfigured rather than storing one in plaintext' },
  { icon: WifiOffOutlinedIcon, label: 'Outbound-only site agent', note: 'No inbound firewall rule at any client site — the agent phones home, nothing phones in' },
  { icon: AccountTreeOutlinedIcon, label: '73 ADRs, 114 tracked controls', note: 'Architecture decisions and compliance controls are written down and versioned, not tribal knowledge' },
  { icon: EngineeringOutlinedIcon, label: 'Dogfooded in production', note: 'Our own MSP practice runs on Nexus today — real tickets, real devices, real clients' },
  { icon: ReportProblemOutlinedIcon, label: 'Incident discipline', note: 'Every SEV-1/2 gets a same-day postmortem and a logged evidence trail, not a retro nobody writes' },
];

export function Assurance(): JSX.Element {
  return (
    <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
      <div className="container-content">
        <Reveal>
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <Chip
              label="How Nexus is built"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Platform assurance, not adjectives.
            </h2>
            <p className="mt-4 text-white/70">
              Any vendor can say &quot;tested&quot; and &quot;secure.&quot; Here is the specific,
              checkable evidence behind those words at Nexus — most of it drawn straight from our
              own public repository, not a claim you have to take on faith.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {evidence.map((e, i) => {
            const Icon = e.icon;
            return (
              <Reveal key={e.label} delay={(i % 3) * 80}>
                <div className="group flex h-full items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <div>
                    <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{e.label}</h3>
                    <p className="mt-1 text-xs text-white/55">{e.note}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-white/40">
          Figures current as of this writing and updated as the platform changes — ask us directly
          for the current numbers rather than assuming a snapshot stays accurate forever.
        </p>
      </div>
    </section>
  );
}
